/**
 * Theme registry and Ant Design bridge tokens.
 *
 * Each theme is defined twice, on purpose and in lockstep:
 * 1. CSS variables in web/tailwind.css ([data-theme] blocks) — consumed by
 *    Tailwind/shadcn components.
 * 2. The antd token map below — consumed by ConfigProvider in app.tsx so
 *    Ant Design screens follow theme switches too.
 *
 * Adding a theme = one [data-theme] block in tailwind.css + one entry here.
 */

export type ThemeId =
  | 'light'
  | 'dark'
  | 'midnight'
  | 'solar'
  | 'forest'
  | 'ocean'
  | 'sakura'
  | 'ember';

export const DARK_THEME_IDS: ThemeId[] = ['dark', 'midnight', 'ocean', 'ember'];

export const THEMES: Array<{
  id: ThemeId;
  label: string;
  /** [surface, accent] swatch pair for the theme switcher dots */
  swatch: [string, string];
}> = [
  { id: 'light', label: 'Light', swatch: ['#ffffff', '#3b6cff'] },
  { id: 'dark', label: 'Dark', swatch: ['#22252b', '#6f8dff'] },
  { id: 'midnight', label: 'Midnight', swatch: ['#0a0d16', '#7c6bff'] },
  { id: 'solar', label: 'Solar', swatch: ['#faf7f2', '#d96f32'] },
  { id: 'forest', label: 'Forest', swatch: ['#ffffff', '#16a34a'] },
  { id: 'ocean', label: 'Ocean', swatch: ['#0d1b2a', '#38bdf8'] },
  { id: 'sakura', label: 'Sakura', swatch: ['#faf6f8', '#ec4899'] },
  { id: 'ember', label: 'Ember', swatch: ['#1c1917', '#fb923c'] },
];

interface AntdThemeBridge {
  dark: boolean;
  token: {
    colorPrimary: string;
    colorInfo: string;
    colorBgBase: string;
    colorBgLayout: string;
    colorBgContainer: string;
    colorBgElevated: string;
    colorTextBase: string;
    colorBorder: string;
    colorBorderSecondary: string;
    borderRadius: number;
  };
}

export const ANTD_THEME_TOKENS: Record<ThemeId, AntdThemeBridge> = {
  light: {
    dark: false,
    token: {
      colorPrimary: '#3b6cff',
      colorInfo: '#3b6cff',
      colorBgBase: '#ffffff',
      colorBgLayout: '#f6f7f9',
      colorBgContainer: '#ffffff',
      colorBgElevated: '#ffffff',
      colorTextBase: '#16181d',
      colorBorder: '#e7eaef',
      colorBorderSecondary: '#eef0f4',
      borderRadius: 12,
    },
  },
  dark: {
    dark: true,
    token: {
      colorPrimary: '#6f8dff',
      colorInfo: '#6f8dff',
      colorBgBase: '#1b1d22',
      colorBgLayout: '#131417',
      colorBgContainer: '#1b1d22',
      colorBgElevated: '#22252b',
      colorTextBase: '#f2f3f5',
      colorBorder: '#2b2e36',
      colorBorderSecondary: '#24262d',
      borderRadius: 12,
    },
  },
  midnight: {
    dark: true,
    token: {
      colorPrimary: '#7c6bff',
      colorInfo: '#7c6bff',
      colorBgBase: '#11162a',
      colorBgLayout: '#0a0d16',
      colorBgContainer: '#131a30',
      colorBgElevated: '#1a2138',
      colorTextBase: '#eef1fb',
      colorBorder: '#283052',
      colorBorderSecondary: '#1f2642',
      borderRadius: 14,
    },
  },
  solar: {
    dark: false,
    token: {
      colorPrimary: '#d96f32',
      colorInfo: '#d96f32',
      colorBgBase: '#ffffff',
      colorBgLayout: '#faf7f2',
      colorBgContainer: '#ffffff',
      colorBgElevated: '#fffdf9',
      colorTextBase: '#2a2722',
      colorBorder: '#ebe4d7',
      colorBorderSecondary: '#f1ece3',
      borderRadius: 10,
    },
  },
  forest: {
    dark: false,
    token: {
      colorPrimary: '#16a34a',
      colorInfo: '#16a34a',
      colorBgBase: '#ffffff',
      colorBgLayout: '#f4f7f5',
      colorBgContainer: '#ffffff',
      colorBgElevated: '#ffffff',
      colorTextBase: '#15201a',
      colorBorder: '#e2e9e4',
      colorBorderSecondary: '#ecf1ed',
      borderRadius: 12,
    },
  },
  ocean: {
    dark: true,
    token: {
      colorPrimary: '#38bdf8',
      colorInfo: '#38bdf8',
      colorBgBase: '#0d1b2a',
      colorBgLayout: '#07121d',
      colorBgContainer: '#0d1b2a',
      colorBgElevated: '#13243a',
      colorTextBase: '#e8f2fb',
      colorBorder: '#1f3349',
      colorBorderSecondary: '#182a3c',
      borderRadius: 12,
    },
  },
  sakura: {
    dark: false,
    token: {
      colorPrimary: '#ec4899',
      colorInfo: '#ec4899',
      colorBgBase: '#ffffff',
      colorBgLayout: '#faf6f8',
      colorBgContainer: '#ffffff',
      colorBgElevated: '#fffdfe',
      colorTextBase: '#231a1f',
      colorBorder: '#efe2e8',
      colorBorderSecondary: '#f5ebf0',
      borderRadius: 12,
    },
  },
  ember: {
    dark: true,
    token: {
      colorPrimary: '#fb923c',
      colorInfo: '#fb923c',
      colorBgBase: '#1c1917',
      colorBgLayout: '#141210',
      colorBgContainer: '#1c1917',
      colorBgElevated: '#25211d',
      colorTextBase: '#f5f1ec',
      colorBorder: '#2e2a25',
      colorBorderSecondary: '#262220',
      borderRadius: 12,
    },
  },
};
